import type { Bedrooms, ZoneResult } from '../types/funnel';

/**
 * Florida coverage PRE-SCREEN by ZIP prefix (owner rules, Oct 2026).
 * Operating base: 6122 W Linebaugh Ave, Tampa, FL 33625.
 *
 * This is a browser pre-screen only. Production must geocode every address server-side, compute ROAD
 * distance from the base (≈120 road miles for nearby automatic booking) and re-apply these same rules.
 * The rules live in `coverageRules` so the backend can load an identical, versioned copy.
 */
export type CoverageTier = 'core' | 'corridor' | 'miami' | 'pending' | 'panhandle' | 'out_of_state';

export const coverageRules = {
  version: 'coverage-2026-10-06.1',
  base: '6122 W Linebaugh Ave, Tampa, FL 33625',
  nearbyRoadMiles: 120,
  /** Tampa Bay, Lakeland, Sarasota/Bradenton, Hernando/Pasco, Ocala, Orlando. */
  core: ['335', '336', '337', '338', '342', '344', '346', '347', '327', '328'],
  /** Approved extended corridors: Fort Myers/Naples, Gainesville/Jacksonville, Orlando toward Daytona. Need 2+ bedrooms and a transportation estimate. */
  corridor: ['339', '341', '326', '320', '322', '321'],
  /** Miami: automatic for 2–4 bedrooms only. */
  miami: ['330', '331', '332'],
  /** Other Florida areas (incl. the inland area the owner mentioned): boundaries not confirmed, so team review. */
  pending: ['329', '333', '334', '349', '340'],
  /** Tallahassee, Panama City, Panhandle: never automatic. */
  panhandle: ['323', '324', '325'],
};

export function tierForZip(zip: string): CoverageTier | null {
  if (!/^\d{5}$/.test(zip)) return null;
  const p = zip.slice(0, 3);
  if (coverageRules.core.includes(p)) return 'core';
  if (coverageRules.corridor.includes(p)) return 'corridor';
  if (coverageRules.miami.includes(p)) return 'miami';
  if (coverageRules.panhandle.includes(p)) return 'panhandle';
  if (coverageRules.pending.includes(p) || p.startsWith('32') || p.startsWith('33') || p.startsWith('34')) return 'pending';
  return 'out_of_state';
}

export interface CoverageResult {
  zone: ZoneResult;
  /** i18n key explaining the result to the customer. */
  reasonKey: 'zoneIncomplete' | 'zoneCore' | 'zoneCorridor' | 'zoneCorridorSmall' | 'zoneMiami' | 'zoneMiamiSmall' | 'zonePending' | 'zonePanhandle' | 'zoneOutside' | 'zoneLarge';
  /** Route goes beyond the local area, so the 1 local travel hour does not cover transportation. */
  needsTransport: boolean;
  tier: CoverageTier | null;
}

const order: CoverageTier[] = ['core', 'corridor', 'miami', 'pending', 'panhandle', 'out_of_state'];

/** Evaluates pickup, delivery and every extra stop. The most restrictive location decides. */
export function evaluateCoverage(zips: string[], size: Bedrooms): CoverageResult {
  const tiers = zips.map(tierForZip);
  if (tiers.length < 2 || tiers.some((t) => t === null)) return { zone: 'incomplete', reasonKey: 'zoneIncomplete', needsTransport: false, tier: null };
  const worst = (tiers as CoverageTier[]).reduce((a, b) => (order.indexOf(b) > order.indexOf(a) ? b : a), 'core');
  const small = size === 'studio' || size === '1' || size === 'few';

  if (worst === 'out_of_state') return { zone: 'outside', reasonKey: 'zoneOutside', needsTransport: true, tier: worst };
  if (worst === 'panhandle') return { zone: 'outside', reasonKey: 'zonePanhandle', needsTransport: true, tier: worst };
  if (worst === 'pending') return { zone: 'review', reasonKey: 'zonePending', needsTransport: true, tier: worst };
  if (size === '5') return { zone: 'review', reasonKey: 'zoneLarge', needsTransport: worst !== 'core', tier: worst };
  if (worst === 'miami') return small ? { zone: 'review', reasonKey: 'zoneMiamiSmall', needsTransport: true, tier: worst } : { zone: 'eligible_prelim', reasonKey: 'zoneMiami', needsTransport: true, tier: worst };
  if (worst === 'corridor') return small ? { zone: 'review', reasonKey: 'zoneCorridorSmall', needsTransport: true, tier: worst } : { zone: 'eligible_prelim', reasonKey: 'zoneCorridor', needsTransport: true, tier: worst };
  return { zone: 'eligible_prelim', reasonKey: 'zoneCore', needsTransport: false, tier: worst };
}
